"use client";

import { useEffect, useState } from "react";
import { useAuth } from "@/context/auth-context";
import { Bell, Clock, CheckCircle, Loader2, Info, Plus, X, Film, Tv } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import { cn } from "@/lib/utils";

export function RequestIndicator() {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const res = await fetch(`/api/requests/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const contentType = res.headers.get("content-type");
      if (!res.ok || !contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error(`Notification fetch failed with status ${res.status}:`, text);
        return;
      }

      const data = await res.json();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Network error fetching notifications:", err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
      return () => clearInterval(interval);
    }
  }, [token]);

  if (!user) return null;

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'plus': return <Plus className="h-3 w-3 text-blue-500" />;
      case 'check': return <CheckCircle className="h-3 w-3 text-green-500" />;
      case 'bell': return <Bell className="h-3 w-3 text-yellow-500" />;
      case 'x': return <X className="h-3 w-3 text-red-500" />;
      case 'clock': return <Clock className="h-3 w-3 text-white/40" />;
      default: return <Info className="h-3 w-3 text-white/20" />;
    }
  };

  const hasUnread = notifications.some(n => {
     const lastSeen = localStorage.getItem('last_seen_notification');
     return !lastSeen || new Date(n.time) > new Date(lastSeen);
  });

  const handleOpenChange = (open: boolean) => {
    if (open && notifications.length > 0) {
      localStorage.setItem('last_seen_notification', new Date().toISOString());
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger nativeButton={false} render={
        <div className="relative h-10 w-10 flex items-center justify-center rounded-full bg-white/5 border border-white/5 hover:bg-white/10 transition-all outline-none group cursor-pointer">
          <Bell className="h-4 w-4 text-white/40 group-hover:text-white transition-colors" />
          {hasUnread && (
            <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-primary shadow-[0_0_10px_rgba(255,255,255,0.5)] animate-pulse" />
          )}
        </div>
      } />
      
      <DropdownMenuContent className="w-80 glass-card p-3" align="end" sideOffset={8}>
        <DropdownMenuLabel className="px-2 py-2 flex items-center justify-between">
           <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/20">Notifications</p>
           {notifications.length > 0 && <span className="text-[9px] font-bold text-white/10">{notifications.length} récents</span>}
        </DropdownMenuLabel>
        
        <div className="space-y-1 mt-2 max-h-96 overflow-y-auto no-scrollbar">
          {notifications.length > 0 ? (
            notifications.map((notif) => (
              <Link key={notif.id} href={notif.status === 'COMPLETED' ? '/library' : '/settings?tab=requests'}>
                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors cursor-pointer group border border-transparent hover:border-white/5">
                   <div className="h-10 w-7 rounded-md bg-white/5 overflow-hidden flex-shrink-0 border border-white/5">
                      {notif.posterPath ? (
                        <img src={notif.posterPath} alt="" className="h-full w-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          {notif.type === 'MOVIE' ? <Film className="h-3 w-3 text-white/10" /> : <Tv className="h-3 w-3 text-white/10" />}
                        </div>
                      )}
                   </div>
                   <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-white/80 leading-snug line-clamp-2">
                        {notif.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {getIcon(notif.icon)}
                        <span className="text-[9px] font-bold text-white/20 uppercase tracking-tight">
                          {new Date(notif.time).toLocaleDateString()}
                        </span>
                      </div>
                   </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="py-10 text-center flex flex-col items-center gap-3">
               <Bell className="h-8 w-8 text-white/5" />
               <p className="text-[10px] font-bold uppercase tracking-widest text-white/10">Aucune notification</p>
            </div>
          )}
        </div>
        
        <DropdownMenuSeparator className="bg-white/5 my-2" />
        <Link href="/settings?tab=requests">
           <div className="p-2 text-center text-[9px] font-black uppercase tracking-widest text-white/20 hover:text-white transition-colors cursor-pointer">
              Voir tout l'historique
           </div>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
